'use strict';

/**
 * Escape for HTML attribute context (double-quoted).
 *
 * @param {string} value Raw value.
 * @return {string} Escaped value.
 */
function escapeAttr(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;');
}

/**
 * Whether the default matches this option (mirrors PHP is_option_selected).
 * Uses englishLabel for the label fallback so translated display labels do not break matching.
 *
 * @param {string} defaultValue Default from payload (English / canonical).
 * @param {string} value        Option value (submitted).
 * @param {string} englishLabel English source label from API (optional; falls back to label).
 * @return {boolean} Whether the option is selected.
 */
function isOptionSelected(defaultValue, value, englishLabel) {
	if (!defaultValue) {
		return false;
	}
	return defaultValue === value || defaultValue === englishLabel;
}

/**
 * Build one option row (label + input + optional deselect) matching PHP markup.
 *
 * @param {Object}  item         Payload item (value, label, searchData).
 * @param {string}  apiKey       Dropdown api key (states, payors, specialties).
 * @param {string}  checkboxName Input name attribute.
 * @param {boolean} singleSelect Single-select checkbox.
 * @param {boolean} checked      Initial checked.
 * @param {number}  formIndex    Index of the form on the page, used to keep
 *                               option element IDs unique across forms.
 * @return {string} HTML
 */
function buildOptionRow(
	item,
	apiKey,
	checkboxName,
	singleSelect,
	checked,
	formIndex,
) {
	const value = item.value;
	const label = item.label;
	const checkboxId =
		'option-' +
		formIndex +
		'-' +
		apiKey +
		'-' +
		String(value).replace(/[^\da-z]/gi, '');
	const multiSelectClass = singleSelect ? '' : 'gt-form-input--multi-select';
	const searchData = escapeAttr(item.searchData || '');
	const singleAttr = singleSelect
		? 'data-search-filter-form-single-select'
		: '';
	const checkedAttr = checked ? 'checked' : '';

	const inputAttrs = [
		`id="${escapeAttr(checkboxId)}"`,
		`name="${escapeAttr(checkboxName)}"`,
		`value="${escapeAttr(value)}"`,
		`class="gt-form-input ${multiSelectClass}"`.trim(),
		`data-search-data="${searchData}"`,
	];
	if (singleAttr) {
		inputAttrs.push(singleAttr);
	}
	if (checkedAttr) {
		inputAttrs.push(checkedAttr);
	}

	const inputHtml = `<input data-search-filter-form-input type="checkbox" ${inputAttrs.join(' ')} />`;

	let deselectHtml = '';
	if (singleSelect) {
		deselectHtml = `<button
        type="button"
        class="gt-form-deselect-button"
        aria-label="Deselect ${escapeAttr(label)}"
        title="Deselect ${escapeAttr(label)}"
        data-search-filter-form-deselect-button
      >
        <svg
			width="12"
			height="11"
			viewBox="0 0 12 11"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			focusable="false"
		>
			<path d="M10.6875 2.21875L7.40625 5.5L10.6875 8.8125C11.0938 9.1875 11.0938 9.84375 10.6875 10.2188C10.3125 10.625 9.65625 10.625 9.28125 10.2188L6 6.9375L2.6875 10.2188C2.3125 10.625 1.65625 10.625 1.28125 10.2188C0.875 9.84375 0.875 9.1875 1.28125 8.8125L4.5625 5.5L1.28125 2.21875C0.875 1.84375 0.875 1.1875 1.28125 0.8125C1.65625 0.40625 2.3125 0.40625 2.6875 0.8125L6 4.09375L9.28125 0.8125C9.65625 0.40625 10.3125 0.40625 10.6875 0.8125C11.0938 1.1875 11.0938 1.84375 10.6875 2.21875Z" fill="#353535"></path>
		</svg>
      </button>`;
	}

	const selectedClass = checked ? ' gt-form-input-wrapper--selected' : '';

	return `<label data-search-filter-form-option class="gt-form-input-wrapper gt-form-input-wrapper--dropdown${selectedClass}" for="${escapeAttr(checkboxId)}">
          ${inputHtml}
          ${escapeHtml(label)}
          ${deselectHtml}
        </label>`;
}

/**
 * @param {string} text Plain text for node body.
 * @return {string} Escaped HTML.
 */
function escapeHtml(text) {
	const div =
		typeof document !== 'undefined' ? document.createElement('div') : null;
	if (div) {
		div.textContent = text;
		return div.innerHTML;
	}
	return String(text)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/**
 * Build HTML for a list of payload items.
 *
 * @param {Array<Object>} items     Items with value, label, searchData.
 * @param {Object}        entry     Dropdown payload entry (singleSelect, defaultValue).
 * @param {string}        apiKey    API key.
 * @param {number}        formIndex Index of the form on the page, used to keep
 *                                  option element IDs unique across forms.
 * @return {string} HTML for the options list.
 */
function buildOptionsHtml(items, entry, apiKey, formIndex) {
	const name = `${apiKey}-options`;
	const singleSelect = !!entry.singleSelect;
	const defaultValue = entry.defaultValue || '';
	let html = '';
	for (const item of items) {
		if (!item || !item.value || !item.label) {
			continue;
		}
		const englishForMatch =
			item.englishLabel !== undefined && item.englishLabel !== ''
				? item.englishLabel
				: item.label;
		const checked = isOptionSelected(defaultValue, item.value, englishForMatch);
		html += buildOptionRow(
			item,
			apiKey,
			name,
			singleSelect,
			checked,
			formIndex,
		);
	}
	return html;
}

/**
 * Hydrate one form from parsed payload.
 *
 * @param {HTMLFormElement} form      Form element.
 * @param {Object}          payload   Payload object.
 * @param {number}          formIndex Index of the form on the page, used to keep
 *                                    option element IDs unique across forms.
 * @return {void}
 */
export function hydrateFormFromPayload(form, payload, formIndex = 0) {
	if (!payload || !payload.dropdowns) {
		return;
	}

	const dropdowns = payload.dropdowns;

	for (const apiKey of Object.keys(dropdowns)) {
		const entry = dropdowns[apiKey];
		const dropdown = form.querySelector(
			`[data-search-filter-form-dropdown="${apiKey}"]`,
		);
		if (!dropdown) {
			continue;
		}

		const fieldset = dropdown.querySelector(
			'[data-search-filter-form-options-root]',
		);
		if (!fieldset) {
			continue;
		}

		if (entry.layout === 'empty') {
			continue;
		}

		if (entry.layout === 'single' && Array.isArray(entry.items)) {
			const target = fieldset.querySelector(
				'[data-search-filter-form-options-target]',
			);
			if (target) {
				target.innerHTML = buildOptionsHtml(
					entry.items,
					entry,
					apiKey,
					formIndex,
				);
			}
			continue;
		}

		if (entry.layout === 'sections' && Array.isArray(entry.sections)) {
			for (const section of entry.sections) {
				if (!section || !section.key) {
					continue;
				}
				const slot = fieldset.querySelector(
					`[data-section-key="${section.key}"]`,
				);
				if (slot && Array.isArray(section.items)) {
					slot.innerHTML = buildOptionsHtml(
						section.items,
						entry,
						apiKey,
						formIndex,
					);
				}
			}
		}
	}
}

/**
 * Parse JSON payloads and hydrate all search filter forms on the page.
 *
 * @return {void}
 */
export function hydrateSearchFilterForms() {
	const scripts = document.querySelectorAll(
		'form[data-search-filter-form] .grow-search-filter-form__payload',
	);

	scripts.forEach((scriptEl, formIndex) => {
		const form = scriptEl.closest('form[data-search-filter-form]');
		if (!form) {
			return;
		}

		let payload;
		try {
			payload = JSON.parse(scriptEl.textContent || '');
		} catch {
			return;
		}

		hydrateFormFromPayload(form, payload, formIndex);
	});
}
